/**
 * O sinal de vida de um rastreador — e a marca de «está sendo rastreado» num veículo.
 *
 * São três estados, e não dois, porque o que se faz em cada um é diferente:
 *
 * - **reportando** (verde): falou há pouco. Nada a fazer.
 * - **silencioso** (âmbar): já falou, parou. Pode ser o carro na garagem ou o aparelho arrancado —
 *   vale olhar, não vale alarmar.
 * - **mudo** (cinza): NUNCA falou. Não é uma falha do aparelho, é uma instalação que não terminou:
 *   faltou o SMS, ou o chip, ou o registro no servidor.
 *
 * Reduzir a «ligado/desligado» juntaria o aparelho que nunca foi instalado com o que parou de
 * responder — e são problemas com soluções opostas.
 */
import { cn } from '@/lib/utils';

export type Situacao = 'reportando' | 'silencioso' | 'mudo';

const ESTADO: Record<Situacao, { cor: string; rotulo: string; ajuda: string }> = {
  reportando: {
    cor: 'bg-success',
    rotulo: 'Reportando',
    ajuda: 'Posição recebida na última hora.',
  },
  silencioso: {
    cor: 'bg-warning',
    rotulo: 'Silencioso',
    ajuda: 'Já reportou, mas está calado há mais de uma hora.',
  },
  mudo: {
    cor: 'bg-text-tertiary',
    rotulo: 'Nunca reportou',
    ajuda: 'A instalação não terminou: falta o SMS, o chip ou o registro no servidor.',
  },
};

/** Quanto tempo faz, em palavra curta — «há 3 min», «há 2 dias». */
export function desdeHumano(seg: number): string {
  if (seg < 120) return `há ${seg}s`;
  if (seg < 7200) return `há ${Math.round(seg / 60)} min`;
  if (seg < 172800) return `há ${Math.round(seg / 3600)} h`;
  return `há ${Math.round(seg / 86400)} dias`;
}

export function SinalRastreio({
  situacao,
  silencioSeg,
  compacto = false,
}: {
  situacao: Situacao;
  silencioSeg?: number | null;
  /** Só a bolinha e o rótulo — para caber numa célula de tabela. */
  compacto?: boolean;
}) {
  const e = ESTADO[situacao] ?? ESTADO.mudo;
  return (
    <span
      className="flex items-center gap-2"
      title={silencioSeg != null ? `${e.ajuda} Última: ${desdeHumano(silencioSeg)}.` : e.ajuda}
    >
      <span
        className={cn(
          'size-2 shrink-0 rounded-full',
          e.cor,
          // O verde pulsa: num painel com vários aparelhos, o que está vivo tem de saltar sem
          // exigir leitura. Os outros dois ficam parados de propósito.
          situacao === 'reportando' && 'animate-pulse',
        )}
      />
      <span className="text-text-secondary">{e.rotulo}</span>
      {!compacto && silencioSeg != null && (
        <span className="tipo-corpo-pequeno text-text-tertiary">{desdeHumano(silencioSeg)}</span>
      )}
    </span>
  );
}

/** A marca discreta de «este veículo está sendo rastreado», para a lista de veículos. */
export function MarcaRastreado({ rastreado }: { rastreado?: boolean }) {
  if (!rastreado) return <span className="text-text-tertiary">—</span>;
  return (
    <span
      className="flex items-center gap-2"
      title="Há um rastreador instalado e com rastreamento ligado neste veículo."
    >
      <span className="size-2 shrink-0 animate-pulse rounded-full bg-success" />
      <span className="text-text-secondary">Rastreado</span>
    </span>
  );
}
